import {
  supabase, uid,
  isRecord, toStringValue, toUnknownList, normalizeStringList,
  safeDeleteStoredImage,
  getStoredImageAsset, sameStoredImageAsset, resolveStoredImageUrl,
  getPublicImageUrl, getStoredImageFocus, normalizeAppImage,
  buildEntityImageColumnsFromInput,
  ENTITY_IMAGE_SELECT,
} from './shared'
import type { RecordLike } from './shared'
import { mapEmbeddedLorebook, normalizeLorebookIdList } from './lorebooks'
import { getStoredAppImage, normalizeImageFocus } from '@/lib/media/imageAssetFields'
import { normalizeOpenings, deriveInitialMessageProjection } from '@/lib/characterOpenings'
import type { Character, ConversationIntro } from '@/types/domain'
import type {
  BrowseCharactersRequest,
  ListFavoriteCharactersRequest,
  PopularTagsRequest,
  CharactersListResponse,
} from '@/types/api'
import type { CharacterCreateData, CharacterUpdateData, SetCharacterFavoriteRequest } from '../../contract'

/**
 * Read normalization for the `intro_messages` column. Preserves the null/absent
 * legacy signal (so consumers can fall back to `initial_message`); any array —
 * including `[]` — becomes a well-formed, ordered `ConversationIntro[]`.
 */
function normalizeIntroMessagesRead(value: unknown): ConversationIntro[] | null {
  if (value == null) return null
  return normalizeOpenings(value)
}

/**
 * Write columns for a direct (non-finalizer) character write. When the caller
 * supplies `intro_messages`, both it and the derived first-nonblank
 * `initial_message` projection are written together so the two fields never
 * drift. When absent, returns no intro columns (legacy write: `initial_message`
 * flows through untouched and `intro_messages` stays at its stored value).
 */
function buildIntroMessageWriteColumns(fields: Partial<CharacterCreateData>): RecordLike {
  if (!Object.prototype.hasOwnProperty.call(fields, 'intro_messages')) return {}
  const intro = normalizeOpenings(fields.intro_messages)
  return {
    intro_messages: intro,
    initial_message: deriveInitialMessageProjection(intro),
  }
}

function normalizeOwnerProfile(value: unknown) {
  if (!value || !isRecord(value)) return null;
  const id = toStringValue(value.id);
  if (!id) return null;
  return {
    id,
    username: toStringValue(value.username),
    display_name: toStringValue(value.display_name),
    avatar_url: toStringValue(value.avatar_url),
    avatar_focus: normalizeImageFocus(value.avatar_focus),
  };
}

function slugifyTag(tag = ''): string {
  return tag
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-') || 'tag';
}

function dedupeAndNormalizeTags(tags: string[] = []) {
  const seen = new Set<string>();
  return (tags || [])
    .map(tag => tag?.toString()?.trim() || '')
    .filter(Boolean)
    .reduce((acc: Array<{ tag: string; tag_slug: string }>, tag) => {
      const slug = slugifyTag(tag);
      if (seen.has(slug)) return acc;
      seen.add(slug);
      acc.push({ tag, tag_slug: slug });
      return acc;
    }, []);
}

/** Translate all character read variants to the same app-facing entity. */
function mapCharacter(raw: unknown, joined: boolean): Character | null {
  if (!isRecord(raw) || raw.id == null || !Number.isFinite(Number(raw.id))) return null
  const joinedTags = toUnknownList(raw.character_tags)
    .map((entry) => isRecord(entry) && isRecord(entry.tags) ? toStringValue(entry.tags.tag) : '')
    .filter(Boolean)
  const tags = joined
    ? Array.from(new Set([...normalizeStringList(toUnknownList(raw.tags)), ...joinedTags]))
    : normalizeStringList(toUnknownList(raw.tags_list ?? raw.tags))
  const lorebooks = (Array.isArray(raw.lorebooks)
    ? raw.lorebooks.filter(isRecord)
    : isRecord(raw.lorebook) ? [raw.lorebook] : []).map(mapEmbeddedLorebook)
  const lorebookIds = normalizeLorebookIdList(
    toUnknownList(raw.lorebook_ids).length
      ? toUnknownList(raw.lorebook_ids)
      : lorebooks.length ? lorebooks.map((lorebook) => lorebook.id)
        : raw.lorebook_id != null ? [raw.lorebook_id] : [],
  )
  const imgpath = resolveStoredImageUrl({
    row: raw, prefix: 'img', fallbackUrl: toStringValue(raw.imgpath), getPublicUrl: getPublicImageUrl,
  })
  const focus = getStoredImageFocus(raw, 'img')
  const image = getStoredAppImage({ ...raw, imgpath }, 'img', { fallbackUrl: imgpath })
    ?? normalizeAppImage(raw.image, { url: imgpath, focus })
  const optionalNumber = (value: unknown): number | undefined => {
    if (value == null) return undefined
    const number = Number(value)
    return Number.isFinite(number) ? number : undefined
  }
  const fields = { ...raw }
  delete fields.username
  delete fields.tags_list
  delete fields.character_tags
  delete fields.character_favorites
  return {
    ...fields,
    id: Number(raw.id),
    user_id: toStringValue(raw.user_id),
    character_name: toStringValue(raw.character_name),
    owner_username: raw.owner_username == null ? undefined : toStringValue(raw.owner_username),
    owner_profile: normalizeOwnerProfile(raw.owner_profile),
    description: toStringValue(raw.description),
    personality: toStringValue(raw.personality),
    scenario: toStringValue(raw.scenario),
    imgpath,
    img_focus: focus,
    image,
    is_public: Boolean(raw.is_public),
    initial_message: toStringValue(raw.initial_message),
    intro_messages: normalizeIntroMessagesRead(raw.intro_messages),
    dialogue_examples: toStringValue(raw.dialogue_examples),
    lorebook_ids: lorebookIds,
    lorebooks,
    lorebook_id: raw.lorebook_id == null ? lorebookIds[0] ?? null : Number(raw.lorebook_id),
    lorebook: isRecord(raw.lorebook) ? mapEmbeddedLorebook(raw.lorebook) : lorebooks[0] ?? null,
    tags,
    created_at: toStringValue(raw.created_at),
    updated_at: toStringValue(raw.updated_at),
    message_count_total: optionalNumber(raw.message_count_total),
    avg_messages_per_conversation: optionalNumber(raw.avg_messages_per_conversation),
    favorites_count: joined ? Number(raw.favorites_count ?? 0) || 0 : optionalNumber(raw.favorites_count),
    is_favorited: joined ? toUnknownList(raw.character_favorites).length > 0 : Boolean(raw.is_favorited),
  }
}

function mapCharacterRowWithTags(row: unknown): Character | null {
  return mapCharacter(row, true)
}

function mapCharacterFromBrowseRow(row: unknown): Character | null {
  return mapCharacter(row, false)
}

function isCharacter(value: Character | null): value is Character {
  return value !== null;
}

async function validateOwnedLorebookIds(userId: string, lorebookIds: number[]): Promise<number[]> {
  if (!lorebookIds.length) return lorebookIds;

  const { data, error } = await supabase
    .from('lorebooks')
    .select('id')
    .eq('user_id', userId)
    .in('id', lorebookIds);

  if (error) throw error;

  const ownedIds = new Set(toUnknownList(data).map((row: unknown) => (isRecord(row) ? Number(row.id) : Number.NaN)));
  if (lorebookIds.some((id) => !ownedIds.has(id))) {
    throw new Error('All attached lorebooks must belong to the character owner');
  }

  return lorebookIds;
}

async function replaceCharacterLorebookLinks(characterId: number | string, lorebookIds: number[]) {
  const { error: deleteError } = await supabase
    .from('character_lorebooks')
    .delete()
    .eq('character_id', characterId);

  if (deleteError) throw deleteError;

  if (!lorebookIds.length) return lorebookIds;

  const rows = lorebookIds.map((lorebookId, index) => ({
    character_id: characterId,
    lorebook_id: lorebookId,
    position: index,
  }));

  const { error: insertError } = await supabase
    .from('character_lorebooks')
    .insert(rows);

  if (insertError) throw insertError;

  return lorebookIds;
}

async function upsertTags(tags: string[] = []) {
  const normalized = dedupeAndNormalizeTags(tags);
  if (!normalized.length) return [];

  const slugs = normalized.map((row) => row.tag_slug).filter(Boolean)
  if (!slugs.length) return []

  const { error: ensureError } = await supabase
    .from('tags')
    .upsert(normalized, { onConflict: 'tag_slug', ignoreDuplicates: true })

  if (ensureError) throw ensureError

  const { data, error: selectError } = await supabase
    .from('tags')
    .select('id, tag, tag_slug')
    .in('tag_slug', slugs)

  if (selectError) throw selectError

  const bySlug = new Map(
    toUnknownList(data)
      .filter(isRecord)
      .map((row) => [toStringValue(row.tag_slug), row] as const),
  );
  return slugs.map((slug) => bySlug.get(slug)).filter(Boolean)
}

async function replaceCharacterTags(characterId: number | string, tags: string[] = []) {
  const { error: delError } = await supabase
    .from('character_tags')
    .delete()
    .eq('character_id', characterId);

  if (delError) throw delError;

  const tagRows = await upsertTags(tags);
  if (!tagRows.length) return;

  const linkRows = tagRows.map((tag) => ({
    character_id: characterId,
    tag_id: isRecord(tag) ? tag.id : undefined,
  }));

  const { error: linkError } = await supabase
    .from('character_tags')
    .insert(linkRows);

  if (linkError) throw linkError;
}

export async function getCharacters(): Promise<CharactersListResponse> {
  const { data, error } = await supabase
    .from('characters')
    .select('*, character_favorites:character_favorites!left ( user_id ), character_tags:character_tags ( tag_id, tags ( tag ) )')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return { characters: toUnknownList(data).map(mapCharacterRowWithTags).filter(isCharacter) };
}

export async function getMyCharacters(): Promise<CharactersListResponse> {
  const userId = await uid();
  const { data, error } = await supabase
    .from('characters')
    .select('*, character_favorites:character_favorites!left ( user_id ), character_tags:character_tags ( tag_id, tags ( tag ) )')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return { characters: toUnknownList(data).map(mapCharacterRowWithTags).filter(isCharacter) };
}

export async function browseCharacters(request: BrowseCharactersRequest = {}) {
  const userId = await uid();
  const pageSize = Number(request?.page_size ?? 24);
  const pageNumber = Number(request?.page_number ?? 1);
  const direction = request?.direction === 'backward' ? 'backward' : 'forward';
  const anchor = request?.anchor === 'end' || request?.anchor === 'start' ? request.anchor : null;
  const ownerUserId = request?.owner_user_id ?? null;
  const rawTags = Array.isArray(request?.tags) ? request.tags : null;
  const tagSlugs = rawTags && rawTags.length
    ? rawTags.map(slugifyTag).filter(Boolean)
    : null;

  const { data, error } = await supabase.rpc('browse_characters_v2', {
    p_user_id: userId,
    p_owner_user_id: ownerUserId,
    p_search_query: request?.search_query ?? null,
    p_tags: tagSlugs,
    p_sort_key: request?.sort_key ?? 'recent',
    p_page_size: pageSize,
    p_page_number: pageNumber,
    p_direction: direction,
    p_anchor: anchor,
    p_cursor_id: request?.cursor_id ?? null,
    p_cursor_value: request?.cursor_value ?? null,
  });

  if (error) throw error;

  const row = Array.isArray(data) ? data[0] : data;
  const rawCharacters = row?.characters ?? [];
  const characters = Array.isArray(rawCharacters)
    ? rawCharacters.map(mapCharacterFromBrowseRow).filter(isCharacter)
    : [];

  return {
    characters,
    total_count: Number(row?.total_count ?? 0),
    has_more: Boolean(row?.has_more),
  };
}

export async function getCharacter(id: number | string): Promise<Character> {
  const characterId = Number(id);
  if (!Number.isFinite(characterId)) throw new Error('Invalid character id');

  const { data, error } = await supabase.rpc('rpc_get_character_v1', {
    p_character_id: characterId,
  });

  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  const characterData = row?.character_data;
  if (!characterData) throw new Error('Character not found');
  const character = mapCharacterFromBrowseRow(characterData);
  if (!character) throw new Error('Character not found');
  return character;
}

export async function setCharacterFavorite(request: SetCharacterFavoriteRequest) {
  const characterId = Number(request?.character_id);
  if (!Number.isFinite(characterId)) throw new Error('Invalid character_id');

  const favorited = Boolean(request?.favorited);
  const { data, error } = await supabase.rpc('rpc_set_character_favorite_v1', {
    p_character_id: characterId,
    p_favorited: favorited,
  });

  if (error) throw error;

  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('Favorite toggle returned no data');

  return {
    character_id: Number(row.character_id),
    is_favorited: Boolean(row.is_favorited),
    favorites_count: Number(row.favorites_count ?? 0) || 0,
  };
}

export async function listFavoriteCharacters(request: ListFavoriteCharactersRequest = {}) {
  const pageSize = Number(request?.page_size ?? 24);
  const cursorId = request?.cursor_id != null ? Number(request.cursor_id) : null;
  const cursorCreatedAt = request?.cursor_created_at ?? null;

  const { data, error } = await supabase.rpc('rpc_list_favorite_characters_v1', {
    p_page_size: pageSize,
    p_cursor_id: cursorId,
    p_cursor_created_at: cursorCreatedAt,
  });

  if (error) throw error;

  const row = Array.isArray(data) ? data[0] : data;
  const rawCharacters = row?.characters ?? [];
  const characters = Array.isArray(rawCharacters)
    ? rawCharacters.map(mapCharacterFromBrowseRow).filter(isCharacter)
    : [];

  return {
    characters,
    total_count: Number(row?.total_count ?? 0),
    has_more: Boolean(row?.has_more),
  };
}

export async function createCharacter(characterData: CharacterCreateData) {
  const userId = await uid();
  const { tags, lorebook_ids, lorebook_id, img_asset, img_focus, ...characterFields } = characterData;
  const imageAssetProvided = Object.prototype.hasOwnProperty.call(characterData, 'img_asset');
  const rawLorebookIds =
    lorebook_ids !== undefined
      ? normalizeLorebookIdList(lorebook_ids)
      : lorebook_id != null
        ? normalizeLorebookIdList([lorebook_id])
        : null;

  try {
    const requestedLorebookIds =
      rawLorebookIds === null ? null : await validateOwnedLorebookIds(userId, rawLorebookIds);
    const payload = {
      ...characterFields,
      ...buildIntroMessageWriteColumns(characterFields),
      ...buildEntityImageColumnsFromInput({
        imgpath: characterFields.imgpath,
        img_asset,
        img_focus,
        imageAssetProvided,
      }),
      user_id: userId,
      ...(requestedLorebookIds !== null ? { lorebook_id: requestedLorebookIds[0] ?? null } : {}),
    };

    const { data, error } = await supabase
      .from('characters')
      .insert(payload)
      .select('*')
      .single();

    if (error) throw error;

    if (tags !== undefined) {
      await replaceCharacterTags(data.id, tags);
    }
    if (requestedLorebookIds !== null) {
      await replaceCharacterLorebookLinks(data.id, requestedLorebookIds);
    }

    const mapped = mapCharacterRowWithTags({
      ...data,
      lorebook_ids: requestedLorebookIds ?? undefined,
      tags,
    });

    return { message: 'Character created successfully', character: mapped };
  } catch (error) {
    if (characterData.img_asset) {
      await safeDeleteStoredImage(characterData.img_asset);
    }
    throw error;
  }
}

export async function updateCharacter(id: number | string, characterData: CharacterUpdateData) {
  const userId = await uid();
  const { tags, lorebook_ids, lorebook_id, img_asset, img_focus, ...characterFields } = characterData;
  const imageAssetProvided = Object.prototype.hasOwnProperty.call(characterData, 'img_asset');
  const previousImage = imageAssetProvided
    ? getStoredImageAsset(
      (
        await supabase
          .from('characters')
          .select(ENTITY_IMAGE_SELECT)
          .eq('id', id)
          .eq('user_id', userId)
          .maybeSingle()
      ).data,
      'img',
    )
    : null;
  const hasLorebookUpdate = lorebook_ids !== undefined || lorebook_id !== undefined;
  const rawLorebookIds =
    lorebook_ids !== undefined
      ? normalizeLorebookIdList(lorebook_ids)
      : lorebook_id != null
        ? normalizeLorebookIdList([lorebook_id])
        : lorebook_id === null
          ? []
          : undefined;

  try {
    const requestedLorebookIds =
      rawLorebookIds === undefined ? undefined : await validateOwnedLorebookIds(userId, rawLorebookIds);
    const payload: RecordLike = {
      ...characterFields,
      ...buildIntroMessageWriteColumns(characterFields),
      ...buildEntityImageColumnsFromInput({
        imgpath: characterFields.imgpath,
        img_asset,
        img_focus,
        imageAssetProvided,
      }),
      ...(hasLorebookUpdate ? { lorebook_id: requestedLorebookIds?.[0] ?? null } : {}),
    };

    if (Object.keys(payload).length > 0) {
      const { error } = await supabase
        .from('characters')
        .update(payload)
        .eq('id', id)
        .eq('user_id', userId);

      if (error) throw error;
    }

    if (tags !== undefined) {
      await replaceCharacterTags(id, tags);
    }
    if (requestedLorebookIds !== undefined) {
      await replaceCharacterLorebookLinks(id, requestedLorebookIds);
    }

    const nextImage = normalizeAppImage(img_asset, {
      url: characterFields.imgpath,
      focus: img_focus,
    });
    const nextImageAsset = nextImage?.asset ?? img_asset;
    const shouldDeletePreviousImage = imageAssetProvided && (!nextImage || Boolean(nextImage.asset));
    if (shouldDeletePreviousImage && previousImage && !sameStoredImageAsset(previousImage, nextImageAsset)) {
      await safeDeleteStoredImage(previousImage);
    }

    return { message: 'Character updated successfully' };
  } catch (error) {
    if (img_asset) {
      await safeDeleteStoredImage(img_asset);
    }
    throw error;
  }
}

export async function deleteCharacter(id: number | string) {
  const userId = await uid();
  const { error } = await supabase
    .from('characters')
    .delete()
    .eq('id', id)
    .eq('user_id', userId);

  if (error) throw error;
  return { message: 'Character deleted successfully' };
}

export async function getTags() {
  const { data, error } = await supabase
    .from('tags')
    .select('tag')
    .order('tag', { ascending: true });

  if (error) throw error;

  const tags = Array.from(new Set(toUnknownList(data).map((row: unknown) => (isRecord(row) ? toStringValue(row.tag).trim() : '')).filter(Boolean)));

  return { tags };
}

export async function getPopularTags(request: PopularTagsRequest = {}) {
  const query = typeof request?.query === 'string' ? request.query.trim() : null
  const limit = Number.isFinite(request?.limit) ? Number(request.limit) : 20

  const { data, error } = await supabase.rpc('get_popular_tags_v1', {
    p_query: query && query.length ? query : null,
    p_limit: limit,
  })

  if (error) throw error

  return {
    tags: toUnknownList(data).map((row: unknown) => ({
      tag: isRecord(row) ? toStringValue(row.tag) : toStringValue(row),
      tag_slug: isRecord(row) ? toStringValue(row.tag_slug) : toStringValue(row),
      use_count: isRecord(row) ? Number(row.use_count ?? 0) || 0 : 0,
    })),
  }
}
